/**
 * Converts Workflowy-style outline lines → workout payload for AddWorkoutDetails.
 *
 * Convention:
 * - depth 0: workout title (first block) and optional extra description lines at depth 0
 * - depth 1: exercise name (placeholders like "Quad exercise" are OK)
 * - depth 2+: prescription / notes — first lines parse as sets (e.g. 3x4 rpe 8); rest → exercise.description
 */

const defaultSet = () => ({
  reps: "",
  weight: "",
  rpe: "",
  time: "",
  timeUnit: "sec",
  distance: "",
  degree: "",
  weightPerSide: false,
  repsPerSide: false,
  backoffPercent: "",
  backoffFromIndex: null,
});

/** Parse strings like "3x4 rpe 8", "4 x 8 @ rpe 7.5" */
export function parsePrescriptionToSets(text) {
  const raw = String(text || "").trim();
  if (!raw) return [];

  const lower = raw.toLowerCase();
  const nxm = lower.match(/(\d+)\s*[x×]\s*(\d+)/);
  if (!nxm) return [];

  const numSets = parseInt(nxm[1], 10);
  const reps = parseInt(nxm[2], 10);
  if (!Number.isFinite(numSets) || !Number.isFinite(reps) || numSets < 1) {
    return [];
  }

  const rpeMatch = lower.match(/rpe\s*(\d+(?:\.\d+)?)/);
  const rpeVal = rpeMatch ? rpeMatch[1] : "";

  const sets = [];
  for (let i = 0; i < numSets; i++) {
    sets.push({
      ...defaultSet(),
      reps: String(reps),
      rpe: rpeVal,
    });
  }
  return sets;
}

function buildExercise(name, prescriptionLines) {
  const lines = (prescriptionLines || []).map((l) => String(l).trim()).filter(Boolean);
  const setLines = [];
  const noteLines = [];
  for (const line of lines) {
    if (parsePrescriptionToSets(line).length > 0) setLines.push(line);
    else noteLines.push(line);
  }

  let sets = [];
  for (const line of setLines) {
    const parsed = parsePrescriptionToSets(line);
    if (parsed.length) sets = sets.concat(parsed);
  }
  if (sets.length === 0) {
    sets = [{ ...defaultSet() }];
  }

  return {
    name: String(name || "").trim() || "Exercise",
    video: "",
    restTimeInput: "",
    weightUnit: "lbs",
    exerciseType: "single",
    comments: [],
    fields: ["REPS", "WEIGHT", "RPE"],
    sets,
    description: noteLines.join("\n"),
  };
}

/**
 * @param {{ id: string, depth: number, text: string }[]} lines — document order
 */
export function outlineToWorkout(lines) {
  const cleaned = (lines || []).map((l) => ({
    ...l,
    text: String(l.text || ""),
    depth: Math.max(0, Math.min(12, Number(l.depth) || 0)),
  }));

  if (cleaned.length === 0) {
    return {
      name: "Workout",
      description: "",
      exercises: [],
    };
  }

  let i = 0;
  let workoutName = "Workout";
  const desc0 = [];

  while (i < cleaned.length && cleaned[i].depth === 0) {
    if (i === 0) workoutName = cleaned[i].text.trim() || workoutName;
    else desc0.push(cleaned[i].text);
    i++;
  }

  const exercises = [];

  while (i < cleaned.length) {
    const row = cleaned[i];
    if (row.depth <= 0) {
      desc0.push(row.text);
      i++;
      continue;
    }
    if (row.depth === 1) {
      const exName = row.text;
      const presc = [];
      i++;
      while (i < cleaned.length && cleaned[i].depth >= 2) {
        presc.push(cleaned[i].text);
        i++;
      }
      exercises.push(buildExercise(exName, presc));
    } else {
      // Leading depth-2+ without a depth-1 parent — treat as orphan notes
      desc0.push(row.text);
      i++;
    }
  }

  return {
    name: workoutName,
    description: desc0.filter(Boolean).join("\n").trim(),
    exercises,
  };
}
